// @ts-nocheck
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../icons';
import { useLocalization, type Language } from '../../../config/localization';

interface Option {
  value: string;
  label: string;
  icon?: string;
}

interface CustomSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: Option[];
  className?: string;
  placeholder?: string;
  language?: Language;
}

const MAX_RENDERED_OPTIONS = 50;

const ChevronDown = () => (
  <svg
    className="w-5 h-5 text-gray-400"
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
  >
    <path
      d="M7 7l3-3 3 3m0 6l-3 3-3-3"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default function CustomSelect({ 
  value, 
  onChange, 
  options, 
  className = '', 
  placeholder = 'Выберите...',
  language = 'en'
}: CustomSelectProps) {
  const t = useLocalization(language);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [position, setPosition] = useState({ x: 0, y: 0, width: 0 });
  const [positionReady, setPositionReady] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);

  const closeDropdown = () => {
    setIsOpen(false);
    setSearchTerm('');
  };


  // Update position when opened
  useEffect(() => {
    const updatePosition = () => {
      if (selectRef.current && isOpen) {
        const rect = selectRef.current.getBoundingClientRect();
        setPosition({
          x: rect.left,
          y: rect.bottom,
          width: rect.width
        });
        setPositionReady(true);
      }
    };

    if (isOpen) {
      setPositionReady(false);
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        updatePosition();
      });
      window.addEventListener('scroll', updatePosition);
      window.addEventListener('resize', updatePosition);
    } else {
      setPositionReady(false);
    }

    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  // Закрытие при клике вне селекта
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      
      // Check if click is inside selectRef
      if (selectRef.current && selectRef.current.contains(target)) {
        return;
      }
      
      // Check if click is inside portal dropdown (has our custom class)
      if (target.closest('.fixed.bg-gray-900')) {
        return;
      }
      
      closeDropdown();
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeDropdown();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isOpen]);

  const selectedOption = options.find(option => option.value === value);
  const hasSearch = options.length > 8;
  const filteredOptions = hasSearch
    ? options.filter(option =>
        String(option.label ?? '').toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;
  const visibleOptions = filteredOptions.slice(0, MAX_RENDERED_OPTIONS);

  useEffect(() => {
    if (!hasSearch && searchTerm) setSearchTerm('');
  }, [hasSearch, searchTerm]);

  const handleOptionClick = (optionValue: string) => {
    onChange(optionValue);
    closeDropdown();
  };

  const mainSelect = (
    <div 
      ref={selectRef}
      className={`relative ${className}`}
      style={{ zIndex: 100, position: 'relative' }}
    >
      {/* Кнопка селекта */}
      <button
        type="button"
        onClick={() => {
          if (isOpen) {
            closeDropdown();
          } else {
            setIsOpen(true);
          }
        }}
        className={`
          w-full px-3 py-2 text-sm text-left rounded-lg
          bg-gray-800 border border-gray-600 text-white
          hover:border-gray-500 focus:border-gray-400
          transition-all duration-200
          focus:outline-none focus:ring-2 focus:ring-gray-500/20
          flex items-center justify-between
          ${isOpen ? 'border-gray-400 ring-2 ring-gray-500/20' : ''}
        `}
        style={{ position: 'relative', zIndex: 150 }}
      >
        <div className="flex items-center gap-2 min-w-0">
          {selectedOption?.icon && (
            <Icon name={selectedOption.icon as any} size={16} className="text-current flex-shrink-0" />
          )}
          <span className="truncate">
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </div>
        <div className="flex-shrink-0">
          <svg className="w-5 h-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </div>
      </button>
    </div>
  );

  // Render dropdown using portal - only when position is ready
  const dropdown = isOpen && positionReady ? (
    <div 
      data-custom-select-dropdown
      className="fixed bg-gray-900 border border-gray-600 rounded-lg shadow-2xl overflow-hidden transition-all duration-200 ease-in-out opacity-100 translate-y-0"
      style={{ 
        left: position.x,
        top: position.y + 4,
        width: position.width,
        zIndex: 999999,
      }}
    >
      {hasSearch && (
        <div className="border-b border-gray-700 p-2">
          <input
            type="text"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder={t.widgets?.searchOptions || 'Search options...'}
            aria-label={t.widgets?.searchOptionsLabel || 'Search options'}
            className="w-full rounded border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-gray-200 placeholder:text-gray-400 focus:border-purple-500 focus:outline-none"
          />
        </div>
      )}
      <ul className="max-h-60 overflow-y-auto cosmic-scrollbar">
        {visibleOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => handleOptionClick(option.value)}
            className={`
              w-full px-3 py-2 text-sm text-left
              hover:bg-gray-700/50 transition-colors duration-150
              focus:outline-none focus:bg-gray-700/50
              first:rounded-t-md last:rounded-b-md
              ${value === option.value ? 'bg-gray-600/60 text-gray-100 font-medium' : 'text-white'}
            `}
          >
            <div className="flex items-center gap-2">
              {option.icon && (
                <Icon name={option.icon as any} size={16} className="text-current flex-shrink-0" />
              )}
              <span>{option.label}</span>
            </div>
          </button>
        ))}
        {filteredOptions.length > MAX_RENDERED_OPTIONS && (
          <li className="px-3 py-2 text-xs text-gray-400" role="status">
            {(t.widgets?.refineOptions || 'Showing the first {count} options. Refine your search.')
              .replace('{count}', String(MAX_RENDERED_OPTIONS))}
          </li>
        )}
      </ul>
    </div>
  ) : null;

  return (
    <>
      {mainSelect}
      {dropdown && createPortal(dropdown, document.body)}
    </>
  );
}
